/**
 * Gráfico de barras mensais — em aberto, pagas e total de contas a pagar.
 */

import React, { useMemo, useState } from 'react'
import { BarChart3 } from 'lucide-react'
import './PayablesMonthlyBarChart.css'

const SERIES = [
  { id: 'open', label: 'Em aberto', colorClass: 'open' },
  { id: 'paid', label: 'Pagas', colorClass: 'paid' },
  { id: 'total', label: 'Total', colorClass: 'total' }
]

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0
  }).format(value || 0)
}

function PayablesMonthlyBarChart({ data }) {
  const [hoveredIndex, setHoveredIndex] = useState(null)

  const totals = useMemo(() => {
    if (!data?.months?.length) return null
    return data.months.reduce(
      (acc, m) => ({
        open: acc.open + (m.open || 0),
        paid: acc.paid + (m.paid || 0),
        total: acc.total + (m.total || 0)
      }),
      { open: 0, paid: 0, total: 0 }
    )
  }, [data])

  const chart = useMemo(() => {
    if (!data?.months?.length) return null

    const months = data.months.map((m, index) => ({ ...m, index }))
    const values = months.flatMap((m) => [m.open || 0, m.paid || 0, m.total || 0])
    const maxValue = Math.max(...values, 1)

    return {
      bars: months.map((m) => ({
        ...m,
        series: SERIES.map((s) => ({
          ...s,
          value: m[s.id] || 0,
          heightPercent: (Math.abs(m[s.id] || 0) / maxValue) * 100
        }))
      }))
    }
  }, [data])

  if (!chart) {
    return (
      <div className="payables-monthly-chart payables-monthly-chart--empty">
        <p>Sem compromissos no período selecionado</p>
      </div>
    )
  }

  const hovered = hoveredIndex !== null ? chart.bars[hoveredIndex] : null

  return (
    <div className="payables-monthly-chart">
      <div className="payables-monthly-chart-header">
        <div>
          <h3 className="payables-monthly-chart-title">
            <BarChart3 size={16} className="payables-monthly-chart-title-icon" />
            Compromissos por mês
          </h3>
          <p className="payables-monthly-chart-subtitle">
            Valores por vencimento no período filtrado
          </p>
        </div>
        <div className="payables-monthly-chart-legend">
          {SERIES.map((s) => (
            <span key={s.id} className="payables-monthly-legend-item">
              <span className={`payables-monthly-legend-swatch ${s.colorClass}`} />
              {s.label}
            </span>
          ))}
        </div>
      </div>

      {totals && (
        <div className="payables-monthly-chart-summary">
          <div>
            <p className="payables-monthly-summary-label">Em aberto</p>
            <p className="payables-monthly-summary-value open">{formatCurrency(totals.open)}</p>
          </div>
          <div>
            <p className="payables-monthly-summary-label">Pagas</p>
            <p className="payables-monthly-summary-value paid">{formatCurrency(totals.paid)}</p>
          </div>
          <div>
            <p className="payables-monthly-summary-label">Total</p>
            <p className="payables-monthly-summary-value total">{formatCurrency(totals.total)}</p>
          </div>
        </div>
      )}

      <div className="payables-monthly-chart-body">
        <div className="payables-monthly-bars">
          {chart.bars.map((bar) => (
            <div
              key={bar.key || bar.index}
              className={`payables-monthly-bar-col${hoveredIndex === bar.index ? ' is-hovered' : ''}`}
              onMouseEnter={() => setHoveredIndex(bar.index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className="payables-monthly-bar-group">
                {bar.series.map((s) => (
                  <div key={s.id} className="payables-monthly-bar-track">
                    <div
                      className={`payables-monthly-bar-fill ${s.colorClass}`}
                      style={{ height: `${Math.max(s.heightPercent, s.value > 0 ? 6 : 0)}%` }}
                    />
                  </div>
                ))}
              </div>
              <span className="payables-monthly-bar-label">{bar.label}</span>
            </div>
          ))}
        </div>

        {hovered && (
          <div className="payables-monthly-tooltip" role="tooltip">
            <p className="payables-monthly-tooltip-title">{hovered.label}</p>
            <div className="payables-monthly-tooltip-grid">
              {SERIES.map((s) => (
                <div key={s.id}>
                  <span className={`payables-monthly-tooltip-key ${s.colorClass}`}>{s.label}</span>
                  <span className="payables-monthly-tooltip-value">{formatCurrency(hovered[s.id])}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!hovered && (
          <p className="payables-monthly-chart-hint">Passe o mouse sobre um mês para ver os valores</p>
        )}
      </div>
    </div>
  )
}

export default PayablesMonthlyBarChart
